import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchMenus } from "../../api/axios";

const BaseUrl = process.env.REACT_APP_UPLOAD_URL;

export default function Navbar({ data }) {
  const [menus, setMenus] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const element = document.getElementById("navbar");
    const handleScroll = () => {
      if (window.scrollY > 170) {
        element.classList.add("is-sticky");
      } else {
        element.classList.remove("is-sticky");
      }
    };
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchMenus().then(setMenus);
  }, []);

  if (!data) return null;

  const { companyName, logo } = data;

  const toggleNavbar = () => setCollapsed(!collapsed);
  const toggleMenu = (key) => {
    setOpenMenu(openMenu === key ? null : key);
    setOpenSubMenu(null);
  };
  const toggleSubMenu = (key) => {
    setOpenSubMenu(openSubMenu === key ? null : key);
  };

  // Normalize path - ensure it starts with / and remove trailing /
  const normalizePath = (path) => {
    if (!path) return "";
    let normalized = path.toString().trim();
    if (!normalized.startsWith("/")) normalized = "/" + normalized;
    if (normalized.length > 1 && normalized.endsWith("/")) {
      normalized = normalized.slice(0, -1);
    }
    return normalized;
  };

  /**
   * Get the slug SEGMENT for a single menu item.
   *
   * Priority: page.slug → menu.slug → menu.url
   *
   * This returns just ONE path segment (e.g. "ui-ux"), NOT the full path.
   * The full path is built by joining all ancestor segments in buildPath().
   *
   * Why page.slug wins:
   *   - Single source of truth: update the Page slug in Strapi once,
   *     and every menu item linking to that page automatically reflects it.
   *   - No need to manually keep the Header Menu slug in sync.
   */
  const getSegment = (menuItem) => {
    if (menuItem?.url === "/") {
      return "/";
    }
    return menuItem?.page?.slug || menuItem?.slug || "";
  };

  /**
   * Build the full href by joining each ancestor's slug segment.
   *
   * Pass menu items from top-level → leaf, e.g:
   *   buildPath(menu)                     → /services
   *   buildPath(menu, child)              → /services/web-design
   *   buildPath(menu, child, grandchild)  → /services/web-design/ui-ux
   *
   * Each level independently resolves: page.slug > own slug
   * So you can mix linked/unlinked items at any level.
   *
   * Example — "Home / Services / UI-UX":
   *   menu.slug      = "services"   (no page linked, uses own slug)
   *   child.slug     = "web-design" (no page linked, uses own slug)
   *   grandchild.page.slug = "ui-ux" (page linked → uses page slug)
   *   Result: /services/web-design/ui-ux  ✓
   */
  const buildPath = (...menuItems) => {
    const segments = menuItems.map(getSegment).filter(Boolean);
    return normalizePath(segments.join("/"));
  };

  const currentPath = normalizePath(location.pathname);

  const contactMenu = menus.find(
    (menu) =>
      ["contact", "contact-us"].includes(menu?.slug?.toLowerCase()) ||
      ["contact", "contact us"].includes(menu?.MenuTitle?.toLowerCase()),
  );

  const normalMenus = menus.filter((menu) => menu.id !== contactMenu?.id);
  // True if this exact path matches the current URL
  const isExactActive = (path) => currentPath === normalizePath(path);

  // True if any child or grandchild is the current page (used to highlight parent dropdown trigger)
  const isChildActive = (menu) => {
    if (!menu.children?.length) return false;
    for (const child of menu.children) {
      if (isExactActive(buildPath(menu, child))) return true;
      if (child.children?.length) {
        for (const grandchild of child.children) {
          if (isExactActive(buildPath(menu, child, grandchild))) return true;
        }
      }
    }
    return false;
  };

  // True if any grandchild is the current page (used to highlight child item in dropdown)
  const isGrandchildActive = (menu, child) => {
    if (!child.children?.length) return false;
    for (const grandchild of child.children) {
      if (isExactActive(buildPath(menu, child, grandchild))) return true;
    }
    return false;
  };

  const classOne = collapsed
    ? "collapse navbar-collapse"
    : "collapse navbar-collapse show";

  const classTwo = collapsed
    ? "navbar-toggler navbar-toggler-right collapsed"
    : "navbar-toggler navbar-toggler-right";

  return (
    <div id="navbar" className="navbar-area">
      <div className="rewy-nav">
        <div className="container">
          <nav className="navbar navbar-expand-lg navbar-light">
            {/* LOGO */}
            {logo ? (
              <Link
                to="/"
                className="navbar-brand"
                onClick={() => setCollapsed(true)}
              >
                <img src={`${BaseUrl}${logo.url}`} alt="logo" />
              </Link>
            ) : (
              <Link to="/" className="navbar-brand">
                {companyName}
              </Link>
            )}

            {/* TOGGLER */}
            <button onClick={toggleNavbar} className={classTwo} type="button">
              <span className="icon-bar top-bar"></span>
              <span className="icon-bar middle-bar"></span>
              <span className="icon-bar bottom-bar"></span>
            </button>

            {/* MENU */}
            <div className={classOne} id="navbarSupportedContent">
              <ul className="navbar-nav">
                {normalMenus.map((menu) => {
                  const hasChildren = menu.children?.length > 0;

                  // ── DROPDOWN MENU ────────────────────────────────────────
                  if (hasChildren) {
                    return (
                      <li className="nav-item has-dropdown" key={menu.id}>
                        <Link
                          to="/#"
                          onClick={(e) => {
                            e.preventDefault();
                            toggleMenu(menu.id);
                          }}
                          className={`nav-link main-dropdown-menu d-flex justify-content-between align-items-center ${
                            isChildActive(menu) ? "active" : ""
                          }`}
                        >
                          <span>
                            {menu.MenuTitle}{" "}
                            <i className="bx bx-chevron-down"></i>
                          </span>
                          <button
                            type="button"
                            className="menu-toggle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMenu(menu.id);
                            }}
                          >
                            {openMenu === menu.id ? "−" : "+"}
                          </button>
                        </Link>

                        {/* LEVEL 2 DROPDOWN */}
                        <ul
                          className={`dropdown-menu ${openMenu === menu.id ? "show" : ""}`}
                        >
                          <div className="dropdown-scroll">
                            {menu.children.map((child) => {
                              const hasGrandchildren =
                                child.children?.length > 0;
                              // /services/web-design  — or if child has a page: /services/<child.page.slug>
                              const childPath = buildPath(menu, child);
                              const isChildPathActive =
                                isExactActive(childPath);

                              return (
                                <li className="nav-item" key={child.id}>
                                  {hasGrandchildren ? (
                                    <>
                                      <Link
                                        to={childPath}
                                        onClick={() => toggleSubMenu(child.id)}
                                        className={`nav-link child-dropdown d-flex justify-content-between align-items-center ${
                                          isChildPathActive ||
                                          isGrandchildActive(menu, child)
                                            ? "active"
                                            : ""
                                        }`}
                                      >
                                        <span>
                                          {child.MenuTitle}{" "}
                                          <i className="bx bx-chevron-down"></i>
                                        </span>
                                        <button
                                          type="button"
                                          className="menu-toggle"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleSubMenu(child.id);
                                          }}
                                        >
                                          {openSubMenu === child.id ? "−" : "+"}
                                        </button>
                                      </Link>

                                      {/* LEVEL 3 DROPDOWN */}
                                      <ul
                                        className={`dropdown-menu grand-child ${openSubMenu === child.id ? "show" : ""}`}
                                      >
                                        {child.children.map((grandchild) => {
                                          // /services/web-design/ui-ux — grandchild.page.slug = "ui-ux"
                                          const grandchildPath = buildPath(
                                            menu,
                                            child,
                                            grandchild,
                                          );
                                          const isGrandchildPathActive =
                                            isExactActive(grandchildPath);

                                          return (
                                            <li
                                              className="nav-item"
                                              key={grandchild.id}
                                            >
                                              <Link
                                                to={grandchildPath}
                                                className={`nav-link ${isGrandchildPathActive ? "active" : ""}`}
                                                onClick={() =>
                                                  setCollapsed(true)
                                                }
                                              >
                                                {grandchild.MenuTitle}
                                              </Link>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </>
                                  ) : (
                                    <Link
                                      to={childPath}
                                      className={`nav-link main-child-dropdown-lev-1 ${isChildPathActive ? "active" : ""}`}
                                      onClick={() => setCollapsed(true)}
                                    >
                                      {child.MenuTitle}
                                    </Link>
                                  )}
                                </li>
                              );
                            })}
                          </div>
                        </ul>
                      </li>
                    );
                  }

                  // ── NORMAL MENU ITEM ─────────────────────────────────────
                  // Top-level, no children: path is just /<segment>
                  // page.slug wins if a page is linked, else menu.slug/url
                  const topPath = buildPath(menu);
                  return (
                    <li className="nav-item" key={menu.id}>
                      {/* <a
                        href={topPath}
                        className={`nav-link main-menu ${isExactActive(topPath) ? "active" : ""}`}
                        onClick={() => setCollapsed(true)}
                      >
                        {menu.MenuTitle}
                      </a> */}
                      <Link
                        to={topPath}
                        className={`nav-link main-menu ${isExactActive(topPath) ? "active" : ""}`}
                        onClick={() => setCollapsed(true)}
                      >
                        {menu.MenuTitle}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              {/* RIGHT BUTTON */}
              {/* <div className="others-option d-flex align-items-center">
                {header?.Button?.label && header?.Button?.url && (
                  <div className="option-item">
                    <a
                      href={`/${header.Button.url}`}
                      className="default-btn"
                      target={header.Button.openInNewTab ? "_blank" : "_self"}
                      rel="noreferrer"
                    >
                      {header.Button.icon?.url ? (
                        <img
                          src={`${process.env.REACT_APP_UPLOAD_URL}${header.Button.icon.url}`}
                          alt="icon"
                          className="btn-icon"
                        />
                      ) : (
                        <i className="bx bx-support"></i>
                      )}
                      {header.Button.label}
                      <span className="default-btn-hover"></span>
                    </a>
                  </div>
                )}
              </div> */}
              {/* RIGHT BUTTON */}
              {contactMenu && (
                <div className="others-option d-flex align-items-center">
                  <div className="option-item">
                    {/* <a
                      href={buildPath(contactMenu)}
                      className="default-btn"
                      onClick={() => setCollapsed(true)}
                    >
                      <i className="bx bx-support"></i>
                      {contactMenu.MenuTitle}
                      <span className="default-btn-hover"></span>
                    </a> */}
                      <Link
                      to={buildPath(contactMenu)}
                      className="default-btn"
                      onClick={() => setCollapsed(true)}
                    >
                      <i className="bx bx-support"></i>
                      {contactMenu.MenuTitle}
                      <span className="default-btn-hover"></span>
                    </Link>
                  </div>
                </div>
              )}{" "}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
